import { db } from '@/lib/firebase'
import { canCreatePost } from '@/lib/utils'
import { getUserCompanies } from '@/services/companies'
import { getUserById } from '@/services/users'
import type { FirestorePost, Post, PostInput } from '@/types'
import { DEFAULT_PAGINATION, type Pagination } from '@/types/pagination'
import { firestorePostSchema } from '@/types/post'
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore'

const POSTS_COLLECTION = 'posts'

export { POSTS_COLLECTION }

export const createPost = async (postData: Omit<FirestorePost, 'id' | 'createdAt' | 'updatedAt'>) => {
  // Validate the input data
  const validatedData = firestorePostSchema.omit({ id: true, createdAt: true, updatedAt: true }).parse(postData)

  // Check if the company is enabled
  if (validatedData.companyId) {
    const companyRef = doc(db, 'companies', validatedData.companyId)
    const companySnap = await getDoc(companyRef)

    if (!companySnap.exists()) {
      throw new Error('Company not found')
    }

    const companyData = companySnap.data()
    if (!companyData.status) {
      throw new Error('This company is disabled. Please contact your PR manager for assistance.')
    }
  }

  const now = Timestamp.now()
  const newPost = {
    ...validatedData,
    createdAt: now,
    updatedAt: now,
  }

  const docRef = await addDoc(collection(db, POSTS_COLLECTION), newPost)
  await updateDoc(docRef, { id: docRef.id })

  const docSnap = await getDoc(docRef)
  return { id: docSnap.id, ...docSnap.data() } as FirestorePost
}

// Legacy createPost function for backward compatibility
export const createPostLegacy = async (postInput: PostInput) => {
  // Also check company status for legacy function
  if (postInput.companyId) {
    const companyRef = doc(db, 'companies', postInput.companyId)
    const companySnap = await getDoc(companyRef)

    if (!companySnap.exists()) {
      throw new Error('Company not found')
    }

    const companyData = companySnap.data()
    if (!companyData.status) {
      throw new Error('This company is disabled. Please contact your PR manager for assistance.')
    }
  }

  const now = Timestamp.now()
  const newPost = { ...postInput, createdAt: now, updatedAt: now }
  const docRef = await addDoc(collection(db, POSTS_COLLECTION), newPost)
  await updateDoc(docRef, { id: docRef.id })
  const docSnap = await getDoc(docRef)
  return { id: docSnap.id, ...docSnap.data() } as Post
}

export const getPostById = async (postID: string) => {
  const result = await fetchPost({ postID })
  return result.post
}

export const updatePost = async (postID: string, postInput: PostInput) => {
  const post = (await fetchPost({ postID })).post
  const now = Timestamp.now()
  const docRef = doc(db, POSTS_COLLECTION, postID || '')
  await updateDoc(docRef, { ...postInput, updatedAt: now })
  return { ...post, ...postInput, updatedAt: now }
}

export const deletePost = async (postID: string) => {
  const docRef = doc(db, POSTS_COLLECTION, postID || '')
  await deleteDoc(docRef)
  return true
}

export const getAllPosts = async (maxItems = 20) => {
  try {
    const q = query(collection(db, POSTS_COLLECTION), orderBy('createdAt', 'desc'), limit(maxItems))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
  } catch (error: any) {
    console.error('Error getting all posts:', error)
    throw new Error(`Failed to get all posts: ${error.message}`)
  }
}

export const getPostsByCompany = async (companyId: string) => {
  try {
    const q = query(collection(db, POSTS_COLLECTION), where('companyId', '==', companyId), orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
  } catch (error: any) {
    console.error('Error getting posts by company:', error)
    throw new Error(`Failed to get posts by company: ${error.message}`)
  }
}

export const getDraftsByCompany = async (companyId: string) => {
  try {
    const q = query(
      collection(db, POSTS_COLLECTION),
      where('companyId', '==', companyId),
      where('status', '==', 'draft'),
      orderBy('createdAt', 'desc'),
    )
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
  } catch (error: any) {
    console.error('Error getting drafts by company:', error)
    throw new Error(`Failed to get drafts by company: ${error.message}`)
  }
}

export const getPublishedPostsByCompany = async (companyId: string) => {
  try {
    const q = query(
      collection(db, POSTS_COLLECTION),
      where('companyId', '==', companyId),
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc'),
    )
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
  } catch (error: any) {
    console.error('Error getting published posts by company:', error)
    throw new Error(`Failed to get published posts by company: ${error.message}`)
  }
}

export const getPostsByAuthor = async (authorID: string) => {
  try {
    const q = query(collection(db, POSTS_COLLECTION), where('authorId', '==', authorID), orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
  } catch (error: any) {
    console.error('Error getting posts by author:', error)
    throw new Error(`Failed to get posts by author: ${error.message}`)
  }
}

export const getPostsFromFollowedUsers = async (userID: string) => {
  try {
    const user = await getUserById(userID)
    const followings = user?.followings || []
    if (followings.length === 0) return []

    const q = query(collection(db, POSTS_COLLECTION), where('authorId', 'in', followings), orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
  } catch (error: any) {
    console.error('Error getting posts from followed users:', error)
    throw new Error(`Failed to get posts from followed users: ${error.message}`)
  }
}

export const getRecentPosts = async (pagination: Pagination = DEFAULT_PAGINATION) => {
  try {
    const q = query(collection(db, POSTS_COLLECTION), orderBy('createdAt', 'desc'), limit(pagination.limit))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
  } catch (error: any) {
    console.error('Error getting recent posts:', error)
    throw new Error(`Failed to get recent posts: ${error.message}`)
  }
}

export const searchPosts = async (searchQuery: string) => {
  try {
    // Note: Firestore doesn't support text search natively
    // We need to fetch all posts and filter client-side
    const q = query(collection(db, POSTS_COLLECTION), orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)
    const lowerQuery = searchQuery.toLowerCase()
    return querySnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
      .filter(
        (post) =>
          post.title.toLowerCase().includes(lowerQuery) ||
          post.description.toLowerCase().includes(lowerQuery) ||
          post.subtitle?.toLowerCase().includes(lowerQuery) ||
          post.content?.toLowerCase().includes(lowerQuery),
      )
  } catch (error: any) {
    console.error('Error searching posts:', error)
    throw new Error(`Failed to search posts: ${error.message}`)
  }
}

export const getPostsCount = async () => {
  const q = query(collection(db, POSTS_COLLECTION))
  const snapshot = await getCountFromServer(q)
  return snapshot.data().count
}

// -----

export const fetchPost = async <T extends { postID: string }>(input: T) => {
  const docRef = doc(db, POSTS_COLLECTION, input.postID)
  const docSnap = await getDoc(docRef)
  if (!docSnap.exists()) {
    throw new Error('Post not found')
  }
  const post = { id: docSnap.id, ...docSnap.data() } as Post
  return { ...input, post }
}

export const getArchivedPostsByCompany = async (companyId: string) => {
  try {
    const q = query(
      collection(db, POSTS_COLLECTION),
      where('companyId', '==', companyId),
      where('status', '==', 'archived'),
      orderBy('createdAt', 'desc'),
    )
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
  } catch (error: any) {
    console.error('Error getting archived posts by company:', error)
    throw new Error(`Failed to get archived posts by company: ${error.message}`)
  }
}

export const searchPostsByTitleOrAuthor = async (searchQuery: string) => {
  try {
    // Note: Firestore doesn't support text search natively
    // We need to fetch posts and filter client-side
    const q = query(collection(db, POSTS_COLLECTION), orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)
    const lowerQuery = searchQuery.toLowerCase()
    return querySnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }) as Post)
      .filter(
        (post) => post.title.toLowerCase().includes(lowerQuery) || post.authorName.toLowerCase().includes(lowerQuery),
      )
  } catch (error: any) {
    console.error('Error searching posts by title or author:', error)
    throw new Error(`Failed to search posts by title or author: ${error.message}`)
  }
}

export const createDraftPost = async (context: {
  authorId: string
  authorName: string
  authorPicUrl: string
  companyId: string
}) => {
  if (!context.authorId || !context.companyId) {
    throw new Error('Missing required fields for draft creation')
  }

  const user = await getUserById(context.authorId)
  if (!user) {
    throw new Error('User not found')
  }

  const { companies, roles } = await getUserCompanies(context.authorId)
  const company = companies.find((c) => c.id === context.companyId) || null
  const userRole = roles.get(context.companyId)?.role || null

  if (!canCreatePost(user, userRole, company)) {
    throw new Error('User does not have permission to create posts')
  }

  const draftPostData = {
    title: '',
    subtitle: '',
    content: '',
    description: '',
    authorId: context.authorId,
    authorName: context.authorName,
    authorPicUrl: context.authorPicUrl,
    imageUrl: '',
    status: 'draft' as const,
    visibility: 'both_media_and_fans' as const,
    companyId: context.companyId,
    attachments: [],
    lastEditTimestamp: Date.now(),
  }

  return createPost(draftPostData)
}
